/**
 * NK FAQ section
 *
 * Custom element `<nk-faq>` that filters its accordion list by:
 *   - Active category pill (data-category attribute on the pill matched against each item)
 *   - Search query (matches against each item's data-search lowercase blob)
 *
 * State lives in two instance properties: `activeCategory` and `query`.
 * Filtering is debounced for typing but immediate for category clicks.
 *
 * Search matches both questions and answer text. When a search query is active
 * and an item matches, the card opens automatically and matched substrings get
 * wrapped in <mark> for highlight. Highlights are removed on clear.
 */

(function () {
  'use strict';

  const SEARCH_DEBOUNCE_MS = 120;
  const ALL_CATEGORY = '__all';

  class NkFaq extends HTMLElement {
    connectedCallback() {
      this.pills = Array.from(this.querySelectorAll('[data-nk-faq-pill]'));
      this.items = Array.from(this.querySelectorAll('[data-nk-faq-item]'));
      this.searchInput = this.querySelector('[data-nk-faq-search]');
      this.clearBtn = this.querySelector('[data-nk-faq-search-clear]');
      this.emptyEl = this.querySelector('[data-nk-faq-empty]');

      this.activeCategory = ALL_CATEGORY;
      this.query = '';
      this.searchDebounceId = null;

      this.cacheOriginalText();
      this.bindEvents();
      this.applyFilters();
    }

    /**
     * Snapshot each card's question + answer HTML so search-highlight can
     * be reversibly applied without losing the merchant's rich text formatting.
     */
    cacheOriginalText() {
      this.items.forEach((item) => {
        const q = item.querySelector('.nk-faq__question');
        const a = item.querySelector('.nk-faq__answer');
        if (q) item._originalQuestionHTML = q.innerHTML;
        if (a) item._originalAnswerHTML = a.innerHTML;
      });
    }

    bindEvents() {
      this.pills.forEach((pill) => {
        pill.addEventListener('click', this.handlePillClick.bind(this));
      });

      if (this.searchInput) {
        this.searchInput.addEventListener('input', this.handleSearchInput.bind(this));
      }
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', this.handleClearClick.bind(this));
      }
    }

    handlePillClick(event) {
      const pill = event.currentTarget;
      const category = pill.dataset.category || ALL_CATEGORY;
      this.setActiveCategory(category);
    }

    setActiveCategory(category) {
      this.activeCategory = category;
      this.pills.forEach((pill) => {
        const isActive = (pill.dataset.category || ALL_CATEGORY) === category;
        pill.classList.toggle('is-active', isActive);
        pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      this.applyFilters();
    }

    handleSearchInput(event) {
      const value = event.target.value;
      this.toggleClearButton(value);
      if (this.searchDebounceId) clearTimeout(this.searchDebounceId);
      this.searchDebounceId = setTimeout(() => {
        this.query = String(value || '').trim().toLowerCase();
        this.applyFilters();
      }, SEARCH_DEBOUNCE_MS);
    }

    handleClearClick() {
      if (!this.searchInput) return;
      this.searchInput.value = '';
      this.toggleClearButton('');
      this.query = '';
      this.applyFilters();
      this.searchInput.focus();
    }

    toggleClearButton(value) {
      if (!this.clearBtn) return;
      this.clearBtn.hidden = String(value || '').length === 0;
    }

    /**
     * Show items that match BOTH the active category AND search query.
     * When a search query is active, auto-expand matching cards and highlight matches.
     * When search clears, only auto-collapse cards the search opened — leave any
     * cards the user manually opened in their current state.
     */
    applyFilters() {
      let visibleCount = 0;
      this.items.forEach((item) => {
        const itemCategory = item.dataset.category || '';
        const matchesCategory = this.activeCategory === ALL_CATEGORY || itemCategory === this.activeCategory;
        const searchBlob = item.dataset.search || '';
        const matchesQuery = this.query === '' || searchBlob.includes(this.query);
        const visible = matchesCategory && matchesQuery;
        item.hidden = !visible;
        if (visible) visibleCount += 1;

        const card = item.querySelector('[data-nk-faq-card]');
        if (!card) return;

        if (this.query && visible) {
          if (!card.open) {
            card.open = true;
            card._nkOpenedBySearch = true;
          }
          this.applyHighlight(item);
        } else {
          this.restoreOriginal(item);
          // Collapse cards that search opened, but leave user-opened cards alone.
          if (!this.query && card._nkOpenedBySearch) {
            card.open = false;
            card._nkOpenedBySearch = false;
          }
        }
      });

      if (this.emptyEl) {
        this.emptyEl.hidden = visibleCount > 0;
      }
    }

    applyHighlight(item) {
      const q = item.querySelector('.nk-faq__question');
      const a = item.querySelector('.nk-faq__answer');
      if (q && item._originalQuestionHTML !== undefined) {
        q.innerHTML = highlightHTML(item._originalQuestionHTML, this.query);
      }
      if (a && item._originalAnswerHTML !== undefined) {
        a.innerHTML = highlightHTML(item._originalAnswerHTML, this.query);
      }
    }

    restoreOriginal(item) {
      const q = item.querySelector('.nk-faq__question');
      const a = item.querySelector('.nk-faq__answer');
      if (q && item._originalQuestionHTML !== undefined) {
        q.innerHTML = item._originalQuestionHTML;
      }
      if (a && item._originalAnswerHTML !== undefined) {
        a.innerHTML = item._originalAnswerHTML;
      }
    }
  }

  if (!customElements.get('nk-faq')) {
    customElements.define('nk-faq', NkFaq);
  }

  /**
   * Wrap occurrences of `term` inside `html` with <mark> tags, but only inside
   * text nodes (so we don't break tag attributes). Returns the new HTML string.
   * Term matching is case-insensitive.
   */
  function highlightHTML(html, term) {
    if (!term) return html;
    const container = document.createElement('div');
    container.innerHTML = html;
    walkTextNodes(container, (textNode) => {
      const text = textNode.nodeValue;
      const lowered = text.toLowerCase();
      const idx = lowered.indexOf(term);
      if (idx === -1) return;

      const frag = document.createDocumentFragment();
      let lastEnd = 0;
      let searchFrom = 0;
      while (searchFrom < lowered.length) {
        const found = lowered.indexOf(term, searchFrom);
        if (found === -1) break;
        if (found > lastEnd) {
          frag.appendChild(document.createTextNode(text.slice(lastEnd, found)));
        }
        const mark = document.createElement('mark');
        mark.textContent = text.slice(found, found + term.length);
        frag.appendChild(mark);
        lastEnd = found + term.length;
        searchFrom = lastEnd;
      }
      if (lastEnd < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastEnd)));
      }
      textNode.parentNode.replaceChild(frag, textNode);
    });
    return container.innerHTML;
  }

  /**
   * Depth-first walk of a node's descendants calling `cb(textNode)` for each
   * non-empty text node. Skips <mark> contents to avoid double-wrapping.
   */
  function walkTextNodes(node, cb) {
    const children = Array.from(node.childNodes);
    children.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        if (child.nodeValue && child.nodeValue.trim() !== '') cb(child);
      } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'MARK') {
        walkTextNodes(child, cb);
      }
    });
  }
})();
